import React from 'react'

import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import { Button, Alert } from '@repo/ui'
import { CopyIcon, DownloadIcon } from 'lucide-react'

import { useSnackbar } from 'components/Snackbar'

interface RecoveryCodesStepProps {
  codes: string[]
  onDone: () => void
}

/**
 * VRG-490 §3 — recovery codes are returned by the API EXACTLY ONCE (first
 * verified factor, or an explicit regenerate). There is no endpoint to read
 * them back, so this is the only place they are ever shown, and `onDone` is
 * a one-way door back to the factors list.
 */
export const RecoveryCodesStep: React.FC<RecoveryCodesStepProps> = ({ codes, onDone }) => {
  const theme = useTheme()
  const { openSnackbar } = useSnackbar()

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      openSnackbar({ severity: 'success', title: 'Recovery codes copied to your clipboard.' })
    } catch {
      openSnackbar({ severity: 'error', title: 'Could not copy. Copy the codes manually instead.' })
    }
  }

  const handleDownload = () => {
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'vera-recovery-codes.txt'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Stack spacing={4}>
      <Alert
        severity="warning"
        description="These codes are shown once and can't be retrieved again. Each one can be used a single time to sign in if you lose access to your other two-factor methods. Store them somewhere safe."
      />

      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 3, bgcolor: theme.palette.background.default }}>
        <Grid container spacing={2}>
          {codes.map(code => (
            <Grid item xs={6} key={code}>
              <Typography variant="body3" sx={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: 0.5 }}>
                {code}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="secondary" fullWidth startIcon={<CopyIcon strokeWidth={1.5} size={16} />} onClick={handleCopyAll}>
          Copy all
        </Button>
        <Button variant="secondary" fullWidth startIcon={<DownloadIcon strokeWidth={1.5} size={16} />} onClick={handleDownload}>
          Download
        </Button>
      </Stack>

      <Button variant="primary" fullWidth onClick={onDone}>
        Done, I've saved these
      </Button>
    </Stack>
  )
}
